import { matchPath } from 'react-router-dom';
import { navLabels } from './components/navLabels';

/**
 * One route per Portal artboard in `design/`, and nothing else.
 *
 * There are eleven boards and nine sidebar rows. Nine routes are the rows,
 * each at `/` plus its key. The other two boards have no row of their own, and
 * the row each one highlights says whose page it is:
 *
 * - `PortalProductForm` highlights Products, so it sits under `/products`.
 * - `PortalRegions` highlights Brand profile, so it sits under `/brand-profile`.
 *
 * `title` is the board's own 26px page heading, verbatim. The one exception is
 * the dashboard, whose board draws "Good morning, Grace": that is a signed-in
 * user's name, so it is data, and `screens/Dashboard.tsx` writes it. The route
 * carries its row's label.
 *
 * `board` names the artboard file, so a route can be traced to its spec.
 */
export type PortalRoute = {
  path: string;
  /** The sidebar row that is active on this route. */
  nav: string;
  title: string;
  board: string;
};

const rowBoards: Record<string, string> = {
  dashboard: 'PortalDashboard',
  products: 'PortalProducts',
  'code-batches': 'PortalCodes',
  reports: 'PortalReports',
  'consumer-reports': 'PortalReportsInbox',
  alerts: 'PortalAlerts',
  team: 'PortalTeam',
  'api-keys': 'PortalApiKeys',
  'brand-profile': 'PortalSettings',
};

/** Where each sidebar row goes. */
export const pathFor = (key: string) => `/${key}`;

export const routes: PortalRoute[] = [
  ...Object.keys(navLabels).map((key) => ({
    path: pathFor(key),
    nav: key,
    title: navLabels[key]!,
    board: rowBoards[key]!,
  })),
  { path: '/products/new', nav: 'products', title: 'New product', board: 'PortalProductForm' },
  {
    path: '/brand-profile/regions',
    nav: 'brand-profile',
    title: 'Distribution regions',
    board: 'PortalRegions',
  },
];

/**
 * Where an invited admin redeems the code, L1. Outside the table: it is drawn
 * only signed out, in place of sign-in, and the invitation email links to it
 * with `?code=`.
 */
export const joinPath = '/join';

/**
 * Demo samples, Q6. Outside the table: no board draws it, D36. Its sidebar
 * row sits after Code batches and is shown only while the org is in demo
 * mode, which the user ruled on 2026-10-08.
 */
export const demoKey = 'demo-samples';
export const demoLabel = 'Demo samples';
export const demoPath = pathFor(demoKey);

/** Where `/` and any path this table does not know are sent. */
export const homePath = pathFor('dashboard');

/** The route a pathname resolves to, tolerating a trailing slash. */
export const routeFor = (pathname: string) =>
  routes.find((route) => matchPath(route.path, pathname));
